import type {MetricWithAttribution} from "web-vitals/attribution";
import {trackWebVital, type WebVitalReport} from "@/main/analytics/analyticsEventsCore";
import {appSurface, resetAppSurfaceForTests} from "@/main/common/AppSurfaceDimension";
import {currentRouteTemplate, toRouteTemplate} from "@/lib/routeTemplate";
import {importChunkWithRetry, importChunkWithRetryAndReload} from "@/lib/lazyWithRetry";

// Field Real-User-Monitoring for Core Web Vitals. Registers Google's official `web-vitals` observers
// and forwards each finalized metric into the existing GA4/GTM dataLayer as a `web_vitals` event.
//
// THE ATTRIBUTION BUILD IS THE POINT, not an upgrade for its own sake. Until 2026-08-12 this imported
// the base build, which reports name/value/rating and nothing else — a thermometer. It could tell us
// INP was 500 ms and never which element the member tapped, which phase of the interaction was slow,
// or which element the LCP even was. Google Search Console had been reporting the whole origin as Poor
// on mobile for weeks (INP > 500 ms and LCP > 4 s, both validations Failed) and this instrument could
// not narrow it by one step. INP especially decomposes into three phases with completely different
// fixes — input delay (main thread busy BEFORE the handler), processing duration (the handler), and
// presentation delay (rendering after it) — so an unattributed INP number licenses every hypothesis at
// once. See designs/08-audits-and-process/2026-08-12-core-web-vitals-instrumentation-outage-and-field-rum-attribution.md
//
// web-vitals is CODE-SPLIT (FI-719, 2026-09-28). It used to be imported statically, on the reasoning that
// `importChunkWithRetryAndReload`'s reload-once fallback is wrong for side-effect-only analytics. That
// trade was made when the library was "a few KB"; it measured 15.1 KB of a startup graph sitting at
// 3036 of its 3037 KB budget, and the reload concern is smaller than it reads: the chunk is requested in
// the same boot tick in which every page island's chunk is requested through the same helper, so the one
// realistic reload trigger (a stale `main_bundle.js` whose chunk hashes are gone) reloads the page
// whether or not this library is split. What is NOT given up: the landing route is still captured
// synchronously at boot, and every observer the library registers is `buffered`, so LCP, FCP,
// TTFB, CLS and INP entries recorded before the chunk arrives are still delivered to it.
//
// The observers themselves are passive (PerformanceObserver) and register unconditionally; a metric
// only leaves the device when consent has enabled GTM by the time it finalizes — `trackWebVital` →
// `pushAnalyticsEvent` already enforces that gate. One consequence worth knowing when reading the
// resulting GA4 data: INP and CLS finalize at `pagehide`, and the deferred GTM loader injects on the
// earliest of first interaction, idle-after-load (3 s timeout) or pagehide — so a bounce inside the
// first ~3 seconds reports nothing, and those are disproportionately the SLOWEST sessions. Treat the
// GA4 p75 as an under-estimate of field pain rather than as a match for CrUX.
// See designs/01-now/2026-07-09-frontend-performance-metrics-field-rum-and-bundle-budget-design.md.

let reportingInstalled = false;

// WHICH ROUTE PRODUCED THIS METRIC — FI-190, added 2026-09-17.
//
// INP and CLS do not finalize when they happen; they finalize at `pagehide`. Until this landed the
// only route on the `web_vitals` event was the one `readPageLocationFields()` stamps at SEND time from
// `window.location`, which in a soft-navigating app is the route the member EXITED on. An INP earned
// by a slow reply button on a discussion page was filed under whatever page the member happened to
// close, so no per-surface field INP or CLS was reportable AT ALL — not under-powered, unanswerable.
// The 2026-09-06 product-performance report responded by simply not printing those numbers per
// surface; the emission was unchanged until now.
//
// SOFT NAVIGATIONS, NATIVELY — r93 D12 (FI-2001), web-vitals ^6, 2026-10-12. FI-190's hand-built route
// TIMELINE (history listeners + an entry-time lookup) is deleted. Every observer registers with
// `reportSoftNavs: true`, so in an engine with the Soft Navigation API each soft navigation is its own
// metric instance (`navigationType: "soft-navigation"`) carrying `navigationURL`, "the URL the metric
// applies to"; the route is read straight off it. Every other metric's `navigationURL` is the document's
// hard-navigation URL (web-vitals 6 `initMetric` fills it from the navigation entry), so a soft-navigating
// session in an engine without the Soft Navigation API files its INP/CLS under the landing route. Known
// trade, accepted by D12. Design: the RUM doc's "As built (r94 round 4)".
//
// FI-2150 (2026-10-13): the "no navigationURL → landing route" fallback this block used to describe was dead
// under web-vitals 6 and is gone. A metric with no usable URL sends NO route (absent, never the exit route).
// A bfcache restore is the one case where `navigationURL` is wrong: the restored metric still carries the
// hard-nav (landing) URL, while the page on screen is whatever route the member had soft-navigated to before
// leaving. Those metrics file under the route captured at the restoring `pageshow`. That is a SERIES BREAK
// for `back-forward-cache` rows (they used to read as the landing route); see the frontend-pm register F9.
//
// Route TEMPLATE and not pathname (`lib/routeTemplate.ts`, shared with the share beacon and the
// unusable-page probe): the raw path makes the dimension unbounded — one GA4 cardinality explosion per
// discussion, and a dimension nobody can group by — and gives a username a second place to land.
// `undefined` until a restore; `null` is a real answer (a path with no template) and must not fall through.
let bfcacheRestoredRoute: string | null | undefined = undefined;
let bfcacheRestoreListener: ((event: PageTransitionEvent) => void) | null = null;

function routeFor(metric: MetricWithAttribution): string | null {
    if (metric.navigationType === "back-forward-cache") {
        return bfcacheRestoredRoute !== undefined ? bfcacheRestoredRoute : currentRouteTemplate();
    }
    const url = metric.navigationURL;
    if (typeof url === "string" && url.length > 0) {
        try {
            return toRouteTemplate(new URL(url, window.location.href).pathname);
        } catch {
            // An unparseable URL is not a route.
        }
    }
    return null;
}

/** Every observer reports soft navigations as their own metric instances (web-vitals ^6). */
const OBSERVER_OPTIONS = {reportSoftNavs: true} as const;

// WHICH SURFACE PRODUCED THIS METRIC — the field half's missing leg, added 2026-08-30.
//
// `MobileShellRuntime.isNativeShell()` had TWELVE consumers before this one and `analytics/` was not
// among them, so every field CWV and INP number this product had ever collected blended the Capacitor
// WebView with desktop Chrome and mobile Safari, irreversibly. Three things followed, and they are why
// one line of dimension was worth a wave of its own:
//
//   * "Is the app slower than the website?" could not be asked of real user data AT ANY SAMPLE SIZE.
//   * After a mobile fix shipped, nothing could say whether the app improved or the web did.
//   * The 2026-08-29 audit's field family inherited the same blindness silently.
//
// Resolved ONCE at install rather than per metric. The answer cannot change within a document — a page
// does not migrate from the WebView to a browser tab — and resolving per metric would call into
// Capacitor five-plus times per page for an invariant answer.
//
// The resolver itself moved to `common/AppSurfaceDimension.ts` on 2026-09-06, when the UI control
// census became a second consumer. It is shared rather than copied for the same reason
// `lib/routeTemplate.ts` is: two copies stop being joinable, and whichever one misses a platform folds
// real app traffic into "web" silently.
//
// The attribution fields (selectors truncated to GA4's 100-character ceiling, the per-metric phase splits) are
// built in `webVitalsAttribution.ts` (FI-2211); see the block below.
function roundMetricValue(name: WebVitalReport["name"], value: number): number {
    // CLS is a small unitless score; keep 4 decimals. All others are milliseconds — integers.
    return name === "CLS" ? Math.round(value * 10_000) / 10_000 : Math.round(value);
}

// THE ATTRIBUTION BUILDER IS A LAZY CHUNK — FI-2211 (rotation follow-up D3, 2026-10-13).
//
// Every per-metric diagnostic (LCP breakdown, INP phases and LoAF script, CLS shift, TTFB phases and Server-Timing,
// bfcache notRestoredReasons) lives in `webVitalsAttribution.ts`, loaded on an idle callback after setup. What stays
// here is what must be read WHEN THE METRIC IS REPORTED: the base fields, the app surface and the route (the FI-2150
// bfcache restore route changes on the next `pageshow`, so it cannot wait for the chunk).
//
// NEVER DROP, NEVER DOUBLE-SEND. Until the chunk arrives each report is buffered with its base fields already built:
//   * chunk loads             -> every buffered metric is sent once, with attribution, and later ones go straight out;
//   * page hides first        -> the buffer is sent at once with BASE fields only (attribution keys are optional, so
//     (`visibilitychange` to     the payload shape is unchanged), and a metric that finalizes while hidden is sent the
//      hidden, or `pagehide`)    same way immediately. INP and CLS finalize exactly there, and a dynamic import started
//                                at pagehide never completes, so waiting would lose them;
//   * chunk fails to load     -> the buffer, and every later metric, goes out base-only.
// The hide listeners are registered at setup, BEFORE the web-vitals chunk registers its own, so on the same target they
// run first: the buffer is already empty when web-vitals reports its final INP/CLS.
type AttributionBuilder = (metric: MetricWithAttribution) => Partial<WebVitalReport>;
type PendingMetric = {metric: MetricWithAttribution; base: WebVitalReport};

let attributionBuilder: AttributionBuilder | null = null;
let attributionBuilderFailed = false;
let attributionBuilderLoad: Promise<void> | null = null;
let pendingMetrics: PendingMetric[] = [];
let pageHiding = false;
let idleHandle: {cancel: () => void} | null = null;
let hideListener: ((event: Event) => void) | null = null;
let showListener: ((event: Event) => void) | null = null;

function baseWebVitalReport(metric: MetricWithAttribution): WebVitalReport {
    return {
        name: metric.name,
        value: roundMetricValue(metric.name, metric.value),
        rating: metric.rating,
        delta: roundMetricValue(metric.name, metric.delta),
        id: metric.id,
        navigationType: metric.navigationType,
        appSurface: appSurface(),
        routeTemplate: routeFor(metric) ?? undefined,
    };
}

function sendWebVital(metric: MetricWithAttribution, base: WebVitalReport): void {
    // Best-effort, never throws; a throwing builder still sends the base fields.
    let attribution: Partial<WebVitalReport> = {};
    try {
        attribution = attributionBuilder ? attributionBuilder(metric) : {};
    } catch {
        attribution = {};
    }
    try {
        trackWebVital({...base, ...attribution});
    } catch {
        // no-op: analytics reporting is best-effort.
    }
}

function flushPendingMetrics(): void {
    const batch = pendingMetrics;
    pendingMetrics = [];
    for (const {metric, base} of batch) {
        sendWebVital(metric, base);
    }
}

function isPageHidden(): boolean {
    return pageHiding || (typeof document !== "undefined" && document.visibilityState === "hidden");
}

function reportWebVital(metric: MetricWithAttribution): void {
    let base: WebVitalReport;
    try {
        base = baseWebVitalReport(metric);
    } catch {
        return; // no-op: analytics reporting is best-effort.
    }
    if (attributionBuilder || attributionBuilderFailed || isPageHidden()) {
        sendWebVital(metric, base);
        return;
    }
    pendingMetrics.push({metric, base});
}

/**
 * Load the attribution builder chunk once and send whatever was buffered. Idempotent; never rejects. Called from an
 * idle callback after setup; exported so a spec can wait for it.
 *
 * FI-2689: RETRY-ONLY (`importChunkWithRetry`), never the reloading helper. This chunk is requested from an idle
 * callback up to 3 s after boot, when the member may be reading or typing, and it only adds attribution to telemetry;
 * a failed fetch must degrade to base-only reports (the `.catch` below), never reload the page under the member.
 */
export function loadWebVitalsAttributionBuilder(): Promise<void> {
    if (!attributionBuilderLoad) {
        attributionBuilderLoad = importChunkWithRetry(
            () => import("@/main/analytics/webVitalsAttribution"),
            "web-vitals-attribution",
        )
            .then(({default: build}) => {
                attributionBuilder = build;
            })
            .catch(() => {
                attributionBuilderFailed = true;
            })
            .then(flushPendingMetrics);
    }
    return attributionBuilderLoad;
}

function scheduleIdle(task: () => void): {cancel: () => void} {
    const idleWindow = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: {timeout: number}) => number;
        cancelIdleCallback?: (id: number) => void;
    };
    if (typeof idleWindow.requestIdleCallback === "function") {
        const id = idleWindow.requestIdleCallback(task, {timeout: 3000});
        return {cancel: () => idleWindow.cancelIdleCallback?.(id)};
    }
    const id = window.setTimeout(task, 1500);
    return {cancel: () => window.clearTimeout(id)};
}

/**
 * Install field RUM once per document. Returns when the observers are registered (after the library
 * chunk loads); the bootstrap does not wait on it, and a failed load reports nothing rather than throwing.
 */
export function setupWebVitalsReporting(): Promise<void> {
    if (reportingInstalled || typeof window === "undefined") {
        return Promise.resolve();
    }
    reportingInstalled = true;

    // FI-2266: web-vitals 4.x calls `Array.prototype.at` inside its own PerformanceObserver callbacks (the attribution
    // AND the plain build), and esbuild does not polyfill libraries, so on Chromium < 92 every page threw an uncaught
    // "this.i.at is not a function". No catch here can reach a library callback, so such an engine gets no RUM at all.
    if (typeof (Array.prototype as { at?: unknown }).at !== "function") {
        return Promise.resolve();
    }

    // FI-2150: the route on screen when a Back/Forward restores this document from the bfcache. Registered before
    // the library chunk loads, so it runs before web-vitals' own `pageshow` handler reports the restored metrics.
    bfcacheRestoreListener = (event: PageTransitionEvent) => {
        if (event.persisted) {
            bfcacheRestoredRoute = currentRouteTemplate();
        }
    };
    window.addEventListener("pageshow", bfcacheRestoreListener, true);

    // FI-2211: hiding before the builder chunk arrived sends the buffer now, base-only.
    hideListener = (event: Event) => {
        if (event.type === "pagehide" || document.visibilityState === "hidden") {
            pageHiding = true;
            flushPendingMetrics();
        }
    };
    showListener = () => {
        if (document.visibilityState === "visible") {
            pageHiding = false;
        }
    };
    window.addEventListener("pagehide", hideListener, true);
    document.addEventListener("visibilitychange", hideListener, true);
    document.addEventListener("visibilitychange", showListener, true);
    window.addEventListener("pageshow", showListener, true);

    idleHandle = scheduleIdle(() => {
        idleHandle = null;
        void loadWebVitalsAttributionBuilder();
    });

    // The `{ default: m }` wrap is the idiom for a module of named exports (the helper is typed for default).
    return importChunkWithRetryAndReload(
        () => import("web-vitals/attribution").then((m) => ({default: m})),
        "web-vitals",
    )
        .then(({default: webVitals}) => {
            webVitals.onLCP(reportWebVital, OBSERVER_OPTIONS);
            webVitals.onINP(reportWebVital, OBSERVER_OPTIONS);
            webVitals.onCLS(reportWebVital, OBSERVER_OPTIONS);
            webVitals.onFCP(reportWebVital, OBSERVER_OPTIONS);
            webVitals.onTTFB(reportWebVital, OBSERVER_OPTIONS);
        })
        .catch(() => {
            // no-op: analytics reporting is best-effort, and a missing metrics library is not a user problem.
        });
}

export function resetWebVitalsReportingForTests(): void {
    reportingInstalled = false;
    bfcacheRestoredRoute = undefined;
    if (typeof window !== "undefined") {
        if (bfcacheRestoreListener) {
            window.removeEventListener("pageshow", bfcacheRestoreListener, true);
        }
        if (hideListener) {
            window.removeEventListener("pagehide", hideListener, true);
            document.removeEventListener("visibilitychange", hideListener, true);
        }
        if (showListener) {
            document.removeEventListener("visibilitychange", showListener, true);
            window.removeEventListener("pageshow", showListener, true);
        }
    }
    bfcacheRestoreListener = null;
    hideListener = null;
    showListener = null;
    idleHandle?.cancel();
    idleHandle = null;
    attributionBuilder = null;
    attributionBuilderFailed = false;
    attributionBuilderLoad = null;
    pendingMetrics = [];
    pageHiding = false;
    resetAppSurfaceForTests();
}
